import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO, { faqSchema } from '../components/SEO';

/* ── useInView hook ──────────────────────────────────────────── */
function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ── Animated counter ────────────────────────────────────────── */
// (Removed: previous performance counters were unverified claims.)

/* ── SOS Phone Simulator ─────────────────────────────────────── */
function SOSSimulator() {
  const [phase, setPhase] = useState(0);
  const timers = useRef([]);

  const clearTimers = () => timers.current.forEach(clearTimeout);

  const trigger = () => {
    if (phase === 3) { setPhase(0); return; }
    if (phase !== 0) return;
    clearTimers();
    setPhase(1);
    timers.current = [
      setTimeout(() => setPhase(2), 2000),
      setTimeout(() => setPhase(3), 3800),
    ];
  };

  useEffect(() => () => clearTimers(), []);

  const now = new Date();
  const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  return (
    <div className="phone-frame" role="group" aria-label="Interactive SOS demo">
      <div className="phone-notch" aria-hidden="true"></div>
      <div className="phone-screen" aria-live="polite">
        <div className="phone-header">
          <span className="phone-header-brand">RAKSHA</span>
          <span className="phone-header-time">{time}</span>
        </div>

        {/* Phase 0 — Idle */}
        {phase === 0 && (
          <div className="sim-idle">
            <div className="sim-user-card">
              <div className="sim-avatar">S</div>
              <div>
                <span className="sim-user-name">Welcome back, Sarah</span>
                <span className="sim-user-status">
                  <i className="bi bi-shield-fill-check" style={{ fontSize: '0.6rem' }}></i>
                  You are protected
                </span>
              </div>
            </div>
            <div className="sim-sos-area">
              <p className="sim-sos-prompt">TAP TO SIMULATE SOS ALERT</p>
              <button className="sim-sos-btn" onClick={trigger} aria-label="Simulate an SOS alert (demo only)">
                <i className="bi bi-shield-exclamation" aria-hidden="true"></i>
                SOS
              </button>
              <p className="sim-sos-tap">Hold in emergency · Tap to demo</p>
            </div>
            <div className="sim-gps">
              <span className="sim-gps-dot"></span>
              GPS ACTIVE · DUBLIN 2
            </div>
          </div>
        )}

        {/* Phase 1 — Sending */}
        {phase === 1 && (
          <div className="sim-phase">
            <div className="sim-user-card">
              <div className="sim-avatar">S</div>
              <div>
                <span className="sim-user-name">SOS ACTIVE</span>
                <span className="sim-user-status" style={{ color: '#f39c12' }}>
                  <i className="bi bi-broadcast" style={{ fontSize: '0.6rem' }}></i>
                  Broadcasting alert…
                </span>
              </div>
            </div>
            <div className="sim-card sim-card-orange" style={{ animationDelay: '0s' }}>
              <div className="sim-card-title">🔔 Alert Sent</div>
              <div className="sim-card-body">Notifying 3 verified members in your area…</div>
            </div>
            <div className="sim-card sim-card-orange" style={{ animationDelay: '0.3s', animation: 'slideInUp 0.4s ease 0.3s both' }}>
              <div className="sim-card-title">📍 Location Locked</div>
              <div className="sim-card-body">GPS coordinates captured — Dublin 2, IE</div>
            </div>
          </div>
        )}

        {/* Phase 2 — Accepted */}
        {phase === 2 && (
          <div className="sim-phase">
            <div className="sim-user-card">
              <div className="sim-avatar" style={{ background: 'linear-gradient(135deg, #2ecc71, #27ae60)' }}>✓</div>
              <div>
                <span className="sim-user-name">Nearby member accepted</span>
                <span className="sim-user-status">
                  <i className="bi bi-geo-alt-fill" style={{ fontSize: '0.6rem' }}></i>
                  0.4 km away · En route
                </span>
              </div>
            </div>
            <div className="sim-card sim-card-green">
              <div className="sim-card-title">✅ Responder Found</div>
              <div className="sim-card-body">A verified community member<br />ETA approx. 4 minutes</div>
            </div>
            <div className="sim-card" style={{ background: 'rgba(52,152,219,0.1)', borderLeft: '3px solid #3498db', animation: 'slideInUp 0.4s ease 0.25s both' }}>
              <div className="sim-card-title" style={{ color: '#fff' }}>🚶 Moving to you</div>
              <div className="sim-card-body">Heading to your alert location</div>
            </div>
          </div>
        )}

        {/* Phase 3 — Confirmed */}
        {phase === 3 && (
          <div className="sim-confirmed">
            <div className="sim-confirmed-icon">✅</div>
            <p className="sim-confirmed-title">A member accepted</p>
            <p className="sim-confirmed-sub">
              A nearby member may be on their way.<br />Call 112/999 if your safety is at risk.
            </p>
            <button className="sim-reset-btn" onClick={() => setPhase(0)}>
              <i className="bi bi-arrow-counterclockwise"></i> Reset Simulation
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Hero Section ────────────────────────────────────────────── */
function HeroSection() {
  return (
    <section className="hero">
      <div className="hero-body">
        <div className="hero-grid">
          {/* Left: Text */}
          <div>
            <div className="hero-badge">
              <i className="bi bi-broadcast"></i>
              Verified Community Support · Ireland · Beta
            </div>
            <h1 className="hero-title">
              A personal safety app<br />
              <span>built for Ireland</span>
            </h1>
            <p className="hero-sub">
              RAKSHA is a community-powered personal safety app for Ireland. Send a one-tap SOS to
              approved people nearby, share your location the moment you raise the alert, and get
              clear guidance to contact 112 or 999 when professional emergency help is needed.
            </p>
            <div className="hero-ctas">
              <a href="#waitlist" className="hero-btn-primary">
                <i className="bi bi-envelope-plus-fill"></i>
                Join the Waitlist
              </a>
              <Link to="/how-it-works" className="hero-btn-secondary">
                See how it works <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
            <div className="hero-trust">
              <span className="hero-trust-item"><i className="bi bi-check-circle-fill"></i> Approved accounts</span>
              <span className="hero-trust-item"><i className="bi bi-check-circle-fill"></i> Location shared only during alerts</span>
              <span className="hero-trust-item"><i className="bi bi-check-circle-fill"></i> Block &amp; report controls</span>
            </div>
            <p className="hero-limitation">
              <i className="bi bi-info-circle-fill"></i>
              RAKSHA does not replace emergency services, and a community response is
              not guaranteed. Always call <strong>112 or 999</strong> in a life-threatening emergency.
            </p>
          </div>

          {/* Right: Phone */}
          <div className="hero-right">
            <div className="hero-phone-wrapper">
              <div className="phone-glow"></div>
              <SOSSimulator />
              <div className="floating-cards" aria-label="Example app notifications">
                <div className="float-card">
                  <div className="float-card-title">🔔 Alert nearby</div>
                  <div className="float-card-body">A member needs help · 0.3 km away</div>
                </div>
                <div className="float-card">
                  <div className="float-card-title">✅ A nearby member responded!</div>
                  <div className="float-card-body">You'll see their name and distance</div>
                </div>
                <div className="float-card">
                  <div className="float-card-title">📍 Location captured</div>
                  <div className="float-card-body">GPS coordinates sent with your alert</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats bar inside hero */}
      <StatsBar />
    </section>
  );
}

/* ── Trust Facts Bar ─────────────────────────────────────────── */
const TRUST_FACTS = [
  { icon: 'bi-patch-check-fill', title: 'Verified accounts', label: 'Identity check before access' },
  { icon: 'bi-geo-alt-fill', title: 'Location on SOS', label: 'Captured only when you tap SOS' },
  { icon: 'bi-shield-slash-fill', title: 'Block & report', label: 'Controls built in' },
  { icon: 'bi-telephone-fill', title: '112 / 999', label: 'Escalation guidance' },
];

function StatsBar() {
  const [ref, inView] = useInView(0.3);
  return (
    <div className="stats-bar" ref={ref}>
      <div className="stats-bar-inner">
        {TRUST_FACTS.map((f, i) => (
          <div className="stat-item" key={f.title} style={inView ? { animation: `fadeInUp 0.5s ease ${i * 0.08}s both` } : { opacity: 0 }}>
            <div className="stat-value"><i className={`bi ${f.icon}`}></i> {f.title}</div>
            <div className="stat-label">{f.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Process Section ─────────────────────────────────────────── */
const STEPS = [
  { num: 1, icon: '👆', title: 'Tap SOS', desc: 'Hit the large red SOS button. Your hardware GPS location coordinates are captured instantly.' },
  { num: 2, icon: '📡', title: 'Alert Sent',    desc: 'All verified RAKSHA network members within range receive an instant push notification.' },
  { num: 3, icon: '🙋', title: 'A Member May Accept', desc: 'A nearby approved member may accept the alert and begin moving towards your GPS pin. A response is not guaranteed.' },
  { num: 4, icon: '📱', title: "You're Notified", desc: "If a member accepts, you receive confirmation with their name and distance from you." },
  { num: 5, icon: '📞', title: 'Contact 112 / 999',  desc: 'For non-confrontational support only. Always contact emergency services when your life or safety is at risk.' },
];

function ProcessSection() {
  const [ref, inView] = useInView(0.15);
  return (
    <section className="rk-section rk-section-light" ref={ref}>
      <div className="rk-container">
        <div className="rk-section-header">
          <span className="rk-section-label">The Process</span>
          <h2 className="rk-section-title">How an SOS Works</h2>
          <p className="rk-section-sub">One tap shares your alert. A nearby member may respond — and you're guided to call 112/999 when it matters.</p>
        </div>
        <div className="process-steps">
          {STEPS.map((s, i) => (
            <div className="step-card" key={s.num} style={inView ? { animation: `fadeInUp 0.5s ease ${i * 0.1}s both` } : { opacity: 0 }}>
              <div className="step-number">{s.num}</div>
              <div className="step-icon">{s.icon}</div>
              <h3 className="step-title">{s.title}</h3>
              <p className="step-desc">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="process-branch">
          <i className="bi bi-signpost-split-fill"></i>
          <span>
            <strong>If no responder is available</strong>, your alert stays active until you cancel it —
            and RAKSHA always prompts you to contact 112/999. A community response is never guaranteed.
          </span>
        </div>
      </div>
    </section>
  );
}

/* ── Features Section ────────────────────────────────────────── */
const FEATURES = [
  { icon: 'bi-shield-exclamation', iconClass: 'feature-icon-red',    title: 'One-Tap SOS Trigger',       desc: 'A single tap on the centralized SOS button initiates your emergency broadcast. Locks your coordinates and contacts the local safety network instantly — no typing, no menus.' },
  { icon: 'bi-patch-check-fill',   iconClass: 'feature-icon-blue',   title: 'Identity-Verified Grid',    desc: 'Zero anonymity. Every account requires official government-issued photo ID vetting before approval. Only trusted, real people populate our network.' },
  { icon: 'bi-bell-fill',          iconClass: 'feature-icon-orange', title: 'Real-Time Push Alerts',     desc: 'Powered by Firebase Cloud Messaging. Responders receive emergency push notifications the second you tap, even if their screen is locked.' },
  { icon: 'bi-heart-pulse-fill',   iconClass: 'feature-icon-green',  title: 'Live Acceptance Updates',       desc: 'If a nearby member accepts your alert, your device updates with their name and estimated arrival time. A community response is not guaranteed, and RAKSHA does not replace emergency services.' },
  { icon: 'bi-clock-history',      iconClass: 'feature-icon-purple', title: 'Alert History Logs',  desc: 'Every safety event is recorded so you can review previous SOS alerts, who accepted them, and how each was resolved.' },
  { icon: 'bi-shield-lock-fill',    iconClass: 'feature-icon-cyan',   title: 'Verified Accounts & Controlled Access',  desc: 'Every member completes an identity check before joining. You connect with approved accounts, not anonymous strangers.' },
];

function FeaturesSection() {
  const [ref, inView] = useInView(0.1);
  return (
    <section className="rk-section rk-section-dark" ref={ref}>
      <div className="rk-container">
        <div className="rk-section-header">
          <span className="rk-section-label rk-section-label-light">Features</span>
          <h2 className="rk-section-title rk-section-title-white">Built For Real Emergencies</h2>
          <p className="rk-section-sub rk-section-sub-white">Clear tools with honest limits — designed to help, not to promise the impossible.</p>
        </div>
        <div className="features-grid">
          {FEATURES.map((f, i) => (
            <div className="feature-card" key={f.title} style={inView ? { animation: `fadeInUp 0.5s ease ${i * 0.08}s both` } : { opacity: 0 }}>
              <div className={`feature-icon ${f.iconClass}`}>
                <i className={`bi ${f.icon}`}></i>
              </div>
              <h3 className="feature-title">{f.title}</h3>
              <p className="feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Personas ────────────────────────────────────────────────── */
const PERSONAS = [
  {
    name: 'Students',
    color: '#3498db',
    icon: '🎓',
    quote: 'Walking back to my apartment after a late library session in Trinity or UCC. One tap gives me instant security.',
  },
  {
    name: 'Lone Workers',
    color: '#e67e22',
    icon: '🦺',
    quote: 'Working alone in isolated or rural counties like Kerry. Having local vetted neighbours ready to support is invaluable.',
  },
  {
    name: 'Elderly & Vulnerable',
    color: '#9b59b6',
    icon: '🧓',
    quote: 'Providing peace of mind for seniors living independently. Vetted local residents can arrive before official services.',
  },
  {
    name: 'Late Commuters',
    color: '#1abc9c',
    icon: '🌙',
    quote: 'Waiting at secluded bus stops or walking home from late shifts. RAKSHA acts as an active, invisible safety guard.',
  },
];

function PersonasSection() {
  const [ref, inView] = useInView(0.15);
  return (
    <section className="rk-section rk-section-light" ref={ref}>
      <div className="rk-container">
        <div className="rk-section-header">
          <span className="rk-section-label">Community Cohesion</span>
          <h2 className="rk-section-title">Who Uses RAKSHA?</h2>
          <p className="rk-section-sub">Example scenarios showing how people across Ireland might use RAKSHA. These are illustrative, not real testimonials.</p>
        </div>
        <div className="personas-grid">
          {PERSONAS.map((p, i) => (
            <div className="persona-card" key={p.name} style={inView ? { animation: `fadeInUp 0.5s ease ${i * 0.1}s both` } : { opacity: 0 }}>
              <div className="persona-avatar-wrap" style={{ background: `${p.color}20`, border: `3px solid ${p.color}40` }}>
                <div style={{ width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.2rem' }}>
                  {p.icon}
                </div>
              </div>
              <h3 className="persona-name">{p.name}</h3>
              <span className="persona-tag">Example scenario</span>
              <p className="persona-quote">{p.quote}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── One App, Both Roles ─────────────────────────────────────── */
function TwoSidedSection() {
  const [ref, inView] = useInView(0.15);
  return (
    <section className="rk-section" id="responders" ref={ref}>
      <div className="rk-container">
        <div className="rk-section-header">
          <span className="rk-section-label">One Community, One App</span>
          <h2 className="rk-section-title">Every member can send and answer an SOS</h2>
          <p className="rk-section-sub">There's just one RAKSHA app and one verification process. Once you're approved, you can raise an alert when you need help — and choose to help others nearby when they do.</p>
        </div>
        <div className="twosided-grid">
          <div className="twosided-card" style={inView ? { animation: 'fadeInUp 0.5s ease both' } : { opacity: 0 }}>
            <div className="twosided-icon twosided-icon-red"><i className="bi bi-person-raised-hand"></i></div>
            <h3 className="twosided-title">When you need help</h3>
            <p className="twosided-desc">
              Send an SOS and share your location with approved members nearby. See whether
              someone acknowledges it — and always get clear guidance to call 112/999.
            </p>
            <ul className="twosided-list">
              <li><i className="bi bi-check2"></i> One-tap SOS shares your location</li>
              <li><i className="bi bi-check2"></i> See if a nearby member accepts</li>
              <li><i className="bi bi-check2"></i> Cancel an alert at any time</li>
              <li><i className="bi bi-check2"></i> 112/999 guidance built in</li>
            </ul>
          </div>

          <div className="twosided-card" style={inView ? { animation: 'fadeInUp 0.5s ease 0.12s both' } : { opacity: 0 }}>
            <div className="twosided-icon twosided-icon-blue"><i className="bi bi-shield-check"></i></div>
            <h3 className="twosided-title">When someone nearby needs help</h3>
            <p className="twosided-desc">
              The same app lets you receive nearby alerts and offer non-confrontational support. You choose
              your availability, and you're never expected to put yourself at risk.
            </p>
            <ul className="twosided-list">
              <li><i className="bi bi-check2"></i> Identity check &amp; code of conduct</li>
              <li><i className="bi bi-check2"></i> No confrontation, no pursuit</li>
              <li><i className="bi bi-check2"></i> Control your availability</li>
              <li><i className="bi bi-check2"></i> Decline any alert, call 112/999</li>
            </ul>
          </div>
        </div>
        <div className="twosided-cta-row">
          <a href="#waitlist" className="twosided-cta twosided-cta-red">
            <i className="bi bi-envelope-plus-fill"></i> Join the Android beta waitlist
          </a>
          <Link to="/responders" className="twosided-cta twosided-cta-ghost">
            <i className="bi bi-people-fill"></i> How helping others works
          </Link>
        </div>
      </div>
    </section>
  );
}


/* ── Trust Section ───────────────────────────────────────────── */
function TrustSection() {
  const [ref, inView] = useInView(0.2);
  return (
    <section className="rk-section rk-section-light" ref={ref}>
      <div className="rk-container">
        <div className="trust-grid">
          {/* SVG Illustration */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <svg width="320" height="320" viewBox="0 0 320 320" fill="none" xmlns="http://www.w3.org/2000/svg" style={inView ? { animation: 'fadeInUp 0.6s ease both' } : { opacity: 0 }}>
              {/* Outer glow rings */}
              <circle cx="160" cy="160" r="150" fill="rgba(231,76,60,0.04)" stroke="rgba(231,76,60,0.1)" strokeWidth="1" />
              <circle cx="160" cy="160" r="120" fill="rgba(231,76,60,0.05)" stroke="rgba(231,76,60,0.12)" strokeWidth="1" />
              {/* Main shield */}
              <path d="M160 50 L230 80 L230 170 Q230 220 160 260 Q90 220 90 170 L90 80 Z" fill="#1a1a2e" stroke="#e74c3c" strokeWidth="3"/>
              <path d="M160 70 L215 95 L215 165 Q215 205 160 240 Q105 205 105 165 L105 95 Z" fill="rgba(231,76,60,0.1)"/>
              {/* Lock icon in center */}
              <rect x="143" y="148" width="34" height="28" rx="5" fill="#e74c3c"/>
              <path d="M151 148 L151 138 Q151 126 160 126 Q169 126 169 138 L169 148" stroke="#e74c3c" strokeWidth="3.5" fill="none" strokeLinecap="round"/>
              <circle cx="160" cy="162" r="4" fill="#fff"/>
              {/* Orbiting icons */}
              <g style={{ transformOrigin: '160px 160px', animation: 'float 5s ease-in-out infinite' }}>
                <circle cx="80" cy="100" r="22" fill="#1a1a2e" stroke="rgba(231,76,60,0.4)" strokeWidth="1.5"/>
                <text x="80" y="106" textAnchor="middle" fontSize="16">🪪</text>
              </g>
              <g style={{ transformOrigin: '160px 160px', animation: 'float 5s ease-in-out infinite 1.2s' }}>
                <circle cx="240" cy="100" r="22" fill="#1a1a2e" stroke="rgba(52,152,219,0.4)" strokeWidth="1.5"/>
                <text x="240" y="106" textAnchor="middle" fontSize="16">📍</text>
              </g>
              <g style={{ transformOrigin: '160px 160px', animation: 'float 5s ease-in-out infinite 2.4s' }}>
                <circle cx="80" cy="230" r="22" fill="#1a1a2e" stroke="rgba(46,204,113,0.4)" strokeWidth="1.5"/>
                <text x="80" y="236" textAnchor="middle" fontSize="16">🔔</text>
              </g>
              <g style={{ transformOrigin: '160px 160px', animation: 'float 5s ease-in-out infinite 3.6s' }}>
                <circle cx="240" cy="230" r="22" fill="#1a1a2e" stroke="rgba(155,89,182,0.4)" strokeWidth="1.5"/>
                <text x="240" y="236" textAnchor="middle" fontSize="16">👁️</text>
              </g>
            </svg>
          </div>

          {/* Text */}
          <div style={inView ? { animation: 'fadeInUp 0.6s ease 0.2s both' } : { opacity: 0 }}>
            <span className="rk-section-label">Safety Standards</span>
            <h2 className="trust-title">Built on Trust and Verified Identity</h2>
            <p className="trust-sub">
              We take physical security and verification seriously from day one. You cannot access the RAKSHA community
              without an identity check. This reduces misuse and helps keep the network safer — but identity checks
              cannot eliminate all risk or guarantee anyone's conduct.
            </p>
            <div className="trust-bullets">
              {[
                'Manually reviewed identity documents',
                'Records encrypted in transit and at rest',
                'No anonymous accounts — ever',
                'Location shared only during an active alert',
              ].map(b => (
                <div className="trust-bullet" key={b}>
                  <div className="trust-bullet-icon"><i className="bi bi-check-lg"></i></div>
                  {b}
                </div>
              ))}
            </div>
            <Link to="/safety" className="hero-btn-secondary" style={{ display: 'inline-flex', marginTop: 26 }}>
              See our Safety &amp; Trust approach <i className="bi bi-arrow-right"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Waitlist Signup CTA ─────────────────────────────────────── */
function WaitlistSection() {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [state, setState] = useState('idle'); // idle | sending | success | error
  const [error, setError] = useState('');

  const API_BASE = process.env.REACT_APP_API_URL || '/api';

  const onChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Please enter your name and a valid email address.');
      return;
    }
    // Optional phone: if provided, must look phone-shaped (7+ digits after stripping spaces/symbols)
    const phoneDigits = form.phone.replace(/[^\d]/g, '');
    if (form.phone.trim() && phoneDigits.length < 7) {
      setError('Please enter a valid phone number or leave the field empty.');
      return;
    }
    setState('sending');
    try {
      const res = await fetch(`${API_BASE}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim() || null,
          source: 'website',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setState('success');
        // Fire a conversion event to Umami so we can measure signup rate per channel.
        // Umami is loaded async — guard against calling before it's ready.
        if (typeof window !== 'undefined' && typeof window.umami?.track === 'function') {
          try {
            window.umami.track('waitlist_signup', {
              hasPhone: !!form.phone.trim(),
            });
          } catch (_) { /* analytics never blocks the user flow */ }
        }
      } else {
        setState('error');
        setError(data.message || 'Could not sign you up. Please try again shortly.');
      }
    } catch {
      setState('error');
      setError('Network error. Please try again in a moment.');
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '13px 16px',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.22)',
    background: 'rgba(255,255,255,0.08)',
    color: '#fff',
    fontSize: '1rem',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.15s ease, background 0.15s ease',
  };
  const labelStyle = {
    display: 'block',
    color: 'rgba(255,255,255,0.92)',
    fontSize: '0.82rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    marginBottom: 8,
  };

  return (
    <section className="download-section" id="waitlist">
      <div className="rk-container" style={{ maxWidth: 560 }}>
        <div
          aria-hidden="true"
          style={{
            width: 68, height: 68, margin: '0 auto 20px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.14)',
            border: '1px solid rgba(255,255,255,0.22)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: '1.8rem',
          }}
        >
          <i className="bi bi-envelope-check-fill"></i>
        </div>
        <h2 className="download-title">Join the RAKSHA Ireland Waitlist</h2>
        <p className="download-sub">
          We're rolling out the Android beta in small, verified batches to keep the community safe.
          Leave your details and we'll email your download link when it's your turn.
        </p>

        {state === 'success' ? (
          <div style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 14, padding: '32px 24px', marginTop: 28, textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, margin: '0 auto 12px', borderRadius: '50%', background: 'rgba(255,255,255,0.16)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '1.6rem' }}>
              <i className="bi bi-check2"></i>
            </div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: 10, color: '#fff' }}>You're on the list</h3>
            <p style={{ color: 'rgba(255,255,255,0.88)', fontSize: '0.95rem', margin: 0, lineHeight: 1.55 }}>
              A confirmation email is on its way to <strong>{form.email}</strong>.<br />
              We'll be in touch as soon as your invite is ready.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} style={{ marginTop: 24, textAlign: 'left' }} noValidate>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="wl-name" style={labelStyle}>
                Full name <span style={{ color: '#ffcccc', fontWeight: 500 }}>*</span>
              </label>
              <input
                id="wl-name" name="name" type="text" required autoComplete="name"
                placeholder="e.g. Aoife O'Sullivan" value={form.name} onChange={onChange}
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="wl-email" style={labelStyle}>
                Email address <span style={{ color: '#ffcccc', fontWeight: 500 }}>*</span>
              </label>
              <input
                id="wl-email" name="email" type="email" required autoComplete="email"
                placeholder="you@example.com" value={form.email} onChange={onChange}
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="wl-phone" style={labelStyle}>
                Phone number <span style={{ color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>(optional)</span>
              </label>
              <input
                id="wl-phone" name="phone" type="tel" autoComplete="tel"
                placeholder="+353 87 123 4567" value={form.phone} onChange={onChange}
                style={inputStyle}
              />
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem', margin: '6px 2px 0' }}>
                Helps us verify Irish signups faster. We'll never call you.
              </p>
            </div>
            {error && (
              <p role="alert" style={{ color: '#ffb4b4', fontSize: '0.88rem', margin: '4px 0 14px' }}>
                <i className="bi bi-exclamation-circle-fill"></i>&nbsp;{error}
              </p>
            )}
            <button
              type="submit" disabled={state === 'sending'}
              style={{
                width: '100%',
                padding: '15px 18px',
                borderRadius: 12,
                border: 'none',
                background: '#fff',
                color: '#d32f2f',
                fontWeight: 700,
                fontSize: '1rem',
                fontFamily: 'inherit',
                letterSpacing: '0.01em',
                cursor: state === 'sending' ? 'wait' : 'pointer',
                opacity: state === 'sending' ? 0.7 : 1,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
              }}
            >
              <i className={`bi ${state === 'sending' ? 'bi-arrow-repeat' : 'bi-envelope-plus-fill'}`}></i>
              {state === 'sending' ? 'Submitting…' : 'Join the Waitlist'}
            </button>
          </form>
        )}

        <p className="download-note" style={{ marginTop: 24 }}>
          Android 7.0+ · iOS coming later · Identity verification required after invite.<br />
          By joining you agree to our <Link to="/privacy-policy" style={{ color: 'rgba(255,255,255,0.9)', textDecoration: 'underline' }}>Privacy Policy</Link>.
          Community response is not guaranteed.
        </p>
      </div>
    </section>
  );
}

/* ── FAQ Section ─────────────────────────────────────────────── */
const FAQS = [
  { q: 'Is RAKSHA a replacement for calling 999?',       a: 'No. RAKSHA does not replace emergency services. It may mobilise nearby community members, but a response is never guaranteed. Always call 999/112 in a life-threatening emergency.' },
  { q: 'Is a community response guaranteed?',            a: 'No. RAKSHA broadcasts your alert to approved nearby members, but whether anyone accepts depends on availability, distance and their own safety. Never rely on RAKSHA instead of emergency services.' },
  { q: 'How long does account approval take?',           a: 'Once you receive your invite from the waitlist and complete signup, our administrative team reviews accounts during published support hours. You will receive a push notification once your identity check is complete and your account is active.' },
  { q: 'What ID do I need to register?',                 a: 'Any government-issued photo ID is accepted — passport, driving licence, or National Age Card. Your document is viewed by the RAKSHA admin team for manual review and handled according to our privacy policy.' },
  { q: 'Does the app drain my battery?',                 a: 'No. RAKSHA only reads your GPS location once, at the moment you trigger an SOS — it does not track you in the background. Sending an alert requires mobile data or Wi-Fi.' },
  { q: 'Can I use RAKSHA outside Ireland?',              a: 'Currently RAKSHA is being tested in Ireland. Availability depends on having enough approved members nearby. Expansion beyond Ireland is on our roadmap.' },
  { q: 'What if no one is nearby to respond?',           a: 'The alert stays active until a member accepts or you cancel it. Because a response is not guaranteed, always contact 999/112 directly in dangerous situations.' },
];

function FAQSection() {
  const [open, setOpen] = useState(null);
  return (
    <section className="rk-section rk-section-light" id="faq">
      <div className="rk-container">
        <div className="rk-section-header">
          <span className="rk-section-label">Questions</span>
          <h2 className="rk-section-title">Frequently Asked Questions</h2>
          <p className="rk-section-sub">Clear, direct answers regarding the operations of the safety grid.</p>
        </div>
        <div className="faq-list">
          {FAQS.map((f, i) => (
            <div className={`faq-item${open === i ? ' open' : ''}`} key={i}>
              <button
                className="faq-question"
                onClick={() => setOpen(open === i ? null : i)}
                aria-expanded={open === i}
                aria-controls={`faq-answer-${i}`}
                id={`faq-question-${i}`}
              >
                {f.q}
                <i className="bi bi-chevron-down faq-chevron" aria-hidden="true"></i>
              </button>
              <div
                className="faq-answer"
                id={`faq-answer-${i}`}
                role="region"
                aria-labelledby={`faq-question-${i}`}
                hidden={open !== i}
              >
                <div className="faq-answer-inner">{f.a}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Home ────────────────────────────────────────────────────── */
export default function Home() {
  return (
    <main>
      <SEO
        title="Personal Safety App Ireland | SOS Alerts & Community Help | RAKSHA"
        description="RAKSHA is a community-powered personal safety app for Ireland. Send SOS alerts, share your location and connect with verified people nearby. Android beta — not a replacement for 112/999."
        path="/"
        jsonLd={faqSchema(FAQS)}
      />
      <HeroSection />
      <ProcessSection />
      <FeaturesSection />
      <PersonasSection />
      <TwoSidedSection />
      <TrustSection />
      <WaitlistSection />
      <FAQSection />
    </main>
  );
}
